'use client';

import { useState, useEffect, useCallback } from 'react';
import { X, Download, Shield, Clock, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'arcondes_exit_popup_dismissed';
const DISMISS_DAYS = 7;
const DELAY_MS = 5000;

export default function ExitIntentPopup() {
  const [visible, setVisible] = useState(false);
  const { i18n } = useTranslation();
  const prefix = i18n.language === 'es' ? '/es' : '';

  const dismiss = useCallback(() => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  }, []);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) {
      const elapsed = Date.now() - Number(dismissed);
      if (elapsed < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;
    }

    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, DELAY_MS);

    const handleMouseLeave = (e: MouseEvent) => {
      if (!armed) return;
      if (e.clientY <= 0) {
        setVisible(true);
        document.removeEventListener('mouseout', handleMouseLeave);
      }
    };

    document.addEventListener('mouseout', handleMouseLeave);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseout', handleMouseLeave);
    };
  }, []);

  if (!visible) return null;

  const isEs = i18n.language === 'es';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={dismiss}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-sm shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-600 transition-colors z-10"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="bg-[#1a2e44] p-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#d4803b]/20 text-[#d4803b] px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-widest mb-4 border border-[#d4803b]/30">
            <FileText className="h-3.5 w-3.5" />
            {isEs ? 'Guia Gratis' : 'Free Guide'}
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {isEs
              ? 'No te vayas sin tu Guia FISP Cycle 10'
              : "Don't Leave Without Your FISP Cycle 10 Guide"}
          </h2>
          <p className="text-white/70 text-sm">
            {isEs
              ? 'Todo lo que necesitas saber sobre plazos, requisitos y multas.'
              : 'Everything you need to know about deadlines, requirements, and fines.'}
          </p>
        </div>

        <div className="p-8">
          <div className="space-y-3 mb-6">
            {[
              {
                icon: Clock,
                text: isEs ? 'Calendario completo de plazos por condado' : 'Complete deadline schedule by borough',
              },
              {
                icon: Shield,
                text: isEs ? 'Requisitos de inspeccion y clasificaciones' : 'Inspection requirements & classifications',
              },
              {
                icon: FileText,
                text: isEs ? 'Multas y como evitarlas' : 'Fines breakdown & how to avoid them',
              },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3 text-sm text-gray-700">
                <item.icon className="h-4 w-4 text-[#d4803b] flex-shrink-0" />
                {item.text}
              </div>
            ))}
          </div>

          <Link
            to={`${prefix}/fisp-guide`}
            onClick={dismiss}
            className="flex items-center justify-center gap-2 w-full bg-[#d4803b] text-white py-3.5 rounded-sm font-semibold text-sm uppercase tracking-wider hover:bg-[#c0722f] transition-colors"
          >
            <Download className="h-4 w-4" />
            {isEs ? 'Descargar Guia Gratis' : 'Download Free Guide'}
          </Link>

          <p className="text-center text-xs text-gray-400 mt-4">
            {isEs ? 'Sin costo. Sin compromiso.' : 'Free. No obligation.'}
          </p>
        </div>
      </div>
    </div>
  );
}
